import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  CalendarDays, Users, Receipt, Clock, ArrowRight, FileText,
  MessageSquare, ClipboardList, Activity, Plus, UserPlus,
  Mic, Send, ChevronRight, CheckCircle2, Bell, MailOpen,
  Calendar, Upload, UserCheck, RefreshCw, Star
} from "lucide-react";

/* ── Helpers ── */
function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const ACTIVITY_ICONS = {
  appointment_booked: { icon: CalendarDays, color: "#205B5A" },
  appointment_completed: { icon: CheckCircle2, color: "#205B5A" },
  appointment_rescheduled: { icon: RefreshCw, color: "#496275" },
  patient_checked_in: { icon: UserCheck, color: "#205B5A" },
  invoice_paid: { icon: Receipt, color: "#205B5A" },
  note_drafted: { icon: FileText, color: "#496275" },
  note_approved: { icon: CheckCircle2, color: "#205B5A" },
  message_posted: { icon: MessageSquare, color: "#496275" },
  file_uploaded: { icon: Upload, color: "#496275" },
  new_patient: { icon: UserPlus, color: "#205B5A" },
};

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

/* ── Stat Card ── */
function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-4 md:p-5" data-testid={testId}>
      <div className="flex items-center gap-3 mb-2.5">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: color + "10" }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} strokeWidth={1.5} />
        </div>
      </div>
      <p className="text-2xl font-medium text-[#1A1B1A]" style={H}>{value}</p>
      <p className="text-[11px] text-[#757675] mt-0.5">{label}</p>
    </div>
  );
}

/* ── Quick Action ── */
function QuickAction({ icon: Icon, label, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-[#205B5A]/[0.04] transition-colors group">
      <div className="w-10 h-10 rounded-xl bg-[#205B5A]/[0.06] flex items-center justify-center group-hover:bg-[#205B5A]/10 transition-colors">
        <Icon className="w-4.5 h-4.5 text-[#205B5A]" strokeWidth={1.5} />
      </div>
      <span className="text-[11px] font-medium text-[#4A4B4A] group-hover:text-[#1A1B1A] transition-colors">{label}</span>
    </button>
  );
}

/* ── Message Board Module ── */
function MessageBoard({ messages, onPost, onRefresh }) {
  const [newMsg, setNewMsg] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replies, setReplies] = useState({});

  const toggleExpand = async (msgId) => {
    if (expanded === msgId) { setExpanded(null); return; }
    setExpanded(msgId);
    if (!replies[msgId]) {
      try {
        const { data } = await api.get("/messages");
        const msg = data.messages.find(m => m.id === msgId);
        if (msg) setReplies(prev => ({ ...prev, [msgId]: msg.replies || [] }));
      } catch {}
    }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;
    await onPost(newMsg);
    setNewMsg("");
  };

  const handleReply = async (msgId) => {
    if (!replyText.trim()) return;
    try {
      const { data } = await api.post(`/messages/${msgId}/replies`, { content: replyText });
      setReplies(prev => ({ ...prev, [msgId]: [...(prev[msgId] || []), data] }));
      setReplyText("");
      onRefresh();
    } catch {}
  };

  return (
    <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5" data-testid="message-board">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#205B5A]" />
          <h2 className="text-base font-semibold text-[#1A1B1A]" style={H}>Message board</h2>
        </div>
      </div>

      {/* Post new */}
      <form onSubmit={handlePost} className="flex gap-2 mb-4">
        <input value={newMsg} onChange={e => setNewMsg(e.target.value)} data-testid="message-input"
          className="flex-1 px-3.5 py-2.5 rounded-xl border border-[#1A1B1A]/8 bg-[#FAF9F6] text-sm text-[#1A1B1A] placeholder-[#757675] focus:outline-none focus:border-[#205B5A] focus:bg-white transition-colors"
          placeholder="Post an update to your team..." />
        <button type="submit" data-testid="post-message-btn"
          className="w-10 h-10 rounded-xl bg-[#205B5A] flex items-center justify-center hover:bg-[#174544] transition-colors flex-shrink-0">
          <Send className="w-4 h-4 text-white" />
        </button>
      </form>

      {/* Messages */}
      <div className="space-y-1">
        {messages?.slice(0, 4).map((msg) => (
          <div key={msg.id} className="rounded-xl hover:bg-[#FAF9F6] transition-colors">
            <button onClick={() => toggleExpand(msg.id)} className="w-full text-left p-3 flex items-start gap-3">
              <div className="w-7 h-7 rounded-full bg-[#205B5A]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[9px] font-semibold text-[#205B5A]">{msg.author_name?.split(" ").map(n => n[0]).join("")}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-semibold text-[#1A1B1A]">{msg.author_name}</span>
                  <span className="text-[10px] text-[#757675]">{timeAgo(msg.created_at)}</span>
                </div>
                <p className="text-sm text-[#4A4B4A] leading-relaxed line-clamp-2">{msg.content}</p>
                {msg.reply_count > 0 && (
                  <span className="text-[10px] text-[#205B5A] font-medium mt-1 inline-block">{msg.reply_count} {msg.reply_count === 1 ? "reply" : "replies"}</span>
                )}
              </div>
            </button>

            {expanded === msg.id && (
              <div className="px-3 pb-3 ml-10">
                {(replies[msg.id] || []).map(r => (
                  <div key={r.id} className="flex items-start gap-2 py-2 border-t border-[#1A1B1A]/[0.04] first:border-0">
                    <div className="w-5 h-5 rounded-full bg-[#496275]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[7px] font-semibold text-[#496275]">{r.author_name?.split(" ").map(n => n[0]).join("")}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5"><span className="text-[10px] font-semibold text-[#1A1B1A]">{r.author_name}</span><span className="text-[9px] text-[#757675]">{timeAgo(r.created_at)}</span></div>
                      <p className="text-xs text-[#4A4B4A] leading-relaxed">{r.content}</p>
                    </div>
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <input value={replyText} onChange={e => setReplyText(e.target.value)} data-testid="reply-input"
                    className="flex-1 px-3 py-1.5 rounded-lg border border-[#1A1B1A]/8 text-xs focus:outline-none focus:border-[#205B5A]" placeholder="Reply..." />
                  <button onClick={() => handleReply(msg.id)} data-testid="send-reply-btn"
                    className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544] px-2">Reply</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {(!messages || messages.length === 0) && (
          <p className="text-sm text-[#757675] py-4 text-center">No messages yet. Post an update to get started.</p>
        )}
      </div>
    </div>
  );
}

/* ── Main Dashboard ── */
export default function DashboardPage() {
  const { user, clinic } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadDashboard = useCallback(() => {
    api.get("/dashboard").then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const postMessage = async (content) => {
    await api.post("/messages", { content });
    loadDashboard();
  };

  if (loading) return (
    <div className="p-6 md:p-8"><div className="animate-pulse space-y-4">
      <div className="h-8 bg-[#1A1B1A]/[0.04] rounded-xl w-48" />
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">{[1,2,3,4,5,6].map(i => <div key={i} className="h-[88px] bg-[#1A1B1A]/[0.04] rounded-2xl" />)}</div>
      <div className="grid lg:grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-64 bg-[#1A1B1A]/[0.04] rounded-2xl" />)}</div>
    </div></div>
  );

  const stats = data?.stats || {};
  const role = user?.role;

  return (
    <div className="p-5 md:p-8 max-w-[1400px]" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-medium text-[#1A1B1A] mb-0.5" style={H}>
            Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}, {user?.full_name?.split(" ")[0]}
          </h1>
          <p className="text-sm text-[#757675]">{clinic?.name}</p>
        </div>
        <div className="hidden md:flex items-center gap-2">
          <span className="text-[10px] text-[#757675] uppercase tracking-wider font-medium">{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</span>
        </div>
      </div>

      {/* ── 1. Clinic Snapshot ── */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6" data-testid="clinic-snapshot">
        <StatCard icon={CalendarDays} label="Appointments today" value={stats.appointments_today || 0} color="#205B5A" testId="stat-appointments" />
        <StatCard icon={UserCheck} label="Checked in" value={stats.checked_in || 0} color="#205B5A" testId="stat-checked-in" />
        <StatCard icon={FileText} label="Draft notes" value={stats.draft_notes || 0} color="#496275" testId="stat-draft-notes" />
        <StatCard icon={Receipt} label="Unpaid invoices" value={stats.unpaid_invoices || 0} color={stats.unpaid_invoices > 0 ? "#C4553A" : "#205B5A"} testId="stat-unpaid" />
        <StatCard icon={MessageSquare} label="Messages" value={stats.unread_messages || 0} color="#496275" testId="stat-messages" />
        <StatCard icon={Activity} label="Recent activity" value={stats.new_activity || 0} color="#205B5A" testId="stat-activity" />
      </div>

      {/* ── 2. Quick Actions ── */}
      <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-3 mb-6" data-testid="quick-actions">
        <div className="flex items-center justify-between overflow-x-auto gap-1">
          <QuickAction icon={UserPlus} label="Add patient" onClick={() => navigate("/app/patients")} testId="qa-add-patient" />
          <QuickAction icon={CalendarDays} label="Book appointment" onClick={() => navigate("/app/appointments")} testId="qa-book-appointment" />
          <QuickAction icon={Mic} label="New treatment note" onClick={() => navigate("/app/appointments")} testId="qa-start-session" />
          <QuickAction icon={Receipt} label="Create invoice" onClick={() => navigate("/app/invoices")} testId="qa-create-invoice" />
          <QuickAction icon={MessageSquare} label="Post message" onClick={() => document.querySelector('[data-testid="message-input"]')?.focus()} testId="qa-post-message" />
        </div>
      </div>

      {/* ── Main Grid ── */}
      <div className="grid lg:grid-cols-12 gap-5">

        {/* ── Left Column (schedule + drafts) ── */}
        <div className="lg:col-span-5 space-y-5">
          {/* Today's Schedule */}
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5" data-testid="today-appointments">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#205B5A]" />
                <h2 className="text-base font-semibold text-[#1A1B1A]" style={H}>Today's schedule</h2>
              </div>
              <Link to="/app/calendar" className="text-[11px] text-[#205B5A] font-medium flex items-center gap-0.5 hover:text-[#174544]">Calendar <ArrowRight className="w-3 h-3" /></Link>
            </div>
            {data?.today_appointments?.length === 0 ? (
              <p className="text-sm text-[#757675] py-6 text-center">No appointments today</p>
            ) : (
              <div className="space-y-1">
                {data?.today_appointments?.slice(0, 8).map((a) => (
                  <Link key={a.id} to={`/app/appointments/${a.id}`} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-[#FAF9F6] transition-colors">
                    <span className="text-[11px] font-mono text-[#757675] w-11 flex-shrink-0">{a.start_time}</span>
                    <div className={`w-0.5 h-7 rounded-full flex-shrink-0 ${a.status === "completed" ? "bg-[#205B5A]" : a.status === "arrived" ? "bg-amber-400" : "bg-[#1A1B1A]/10"}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#1A1B1A] truncate">{a.patient_name}</p>
                      <p className="text-[11px] text-[#757675]">{a.appointment_type_name} · {a.practitioner_name}</p>
                    </div>
                    <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full capitalize ${
                      a.status === "completed" ? "bg-[#205B5A]/10 text-[#205B5A]" :
                      a.status === "arrived" ? "bg-amber-50 text-amber-600" :
                      a.status === "cancelled" ? "bg-red-50 text-red-500" :
                      "bg-[#1A1B1A]/[0.04] text-[#757675]"
                    }`}>{a.status}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Draft Treatment Notes */}
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5" data-testid="draft-notes">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#496275]" />
                <h2 className="text-base font-semibold text-[#1A1B1A]" style={H}>Draft notes</h2>
              </div>
            </div>
            {data?.draft_notes?.length === 0 ? (
              <p className="text-sm text-[#757675] py-4 text-center">No pending draft notes</p>
            ) : (
              <div className="space-y-1">
                {data?.draft_notes?.map((dn) => (
                  <Link key={dn.id} to={dn.appointment_id ? `/app/appointments/${dn.appointment_id}` : "#"}
                    className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-[#FAF9F6] transition-colors">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#1A1B1A]">{dn.patient_name}</p>
                      <p className="text-[11px] text-[#757675]">{dn.practitioner_name} · {timeAgo(dn.created_at)}</p>
                    </div>
                    <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full capitalize ${
                      dn.status === "in_review" ? "bg-amber-50 text-amber-600" : "bg-[#496275]/10 text-[#496275]"
                    }`}>{dn.status === "in_review" ? "Review" : "Draft"}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-[#757675]" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Unpaid Invoices */}
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5" data-testid="unpaid-invoices">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Receipt className="w-4 h-4 text-[#C4553A]" />
                <h2 className="text-base font-semibold text-[#1A1B1A]" style={H}>Unpaid invoices</h2>
              </div>
              <Link to="/app/invoices" className="text-[11px] text-[#205B5A] font-medium flex items-center gap-0.5 hover:text-[#174544]">View all <ArrowRight className="w-3 h-3" /></Link>
            </div>
            {data?.unpaid_invoices?.length === 0 ? (
              <p className="text-sm text-[#757675] py-4 text-center">All invoices paid</p>
            ) : (
              <div className="space-y-1">
                {data?.unpaid_invoices?.slice(0, 5).map((inv) => (
                  <Link key={inv.id} to={`/app/invoices/${inv.id}`} className="flex items-center justify-between p-2.5 rounded-xl hover:bg-[#FAF9F6] transition-colors">
                    <div>
                      <p className="text-sm font-medium text-[#1A1B1A]">{inv.patient_name}</p>
                      <p className="text-[11px] text-[#757675]">{inv.invoice_number}</p>
                    </div>
                    <span className="text-sm font-semibold text-[#1A1B1A]">£{inv.total?.toFixed(2)}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Center Column (messages) ── */}
        <div className="lg:col-span-4 space-y-5">
          <MessageBoard messages={data?.messages} onPost={postMessage} onRefresh={loadDashboard} />
        </div>

        {/* ── Right Column (activity) ── */}
        <div className="lg:col-span-3 space-y-5">
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5" data-testid="activity-feed">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-4 h-4 text-[#205B5A]" />
              <h2 className="text-base font-semibold text-[#1A1B1A]" style={H}>Activity</h2>
            </div>
            <div className="space-y-0.5">
              {data?.activities?.slice(0, 12).map((act) => {
                const config = ACTIVITY_ICONS[act.type] || { icon: Bell, color: "#757675" };
                const ActIcon = config.icon;
                return (
                  <div key={act.id} className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-[#FAF9F6] transition-colors">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: config.color + "0D" }}>
                      <ActIcon className="w-3 h-3" style={{ color: config.color }} strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-[#4A4B4A] leading-relaxed">{act.description}</p>
                      <span className="text-[10px] text-[#757675]">{timeAgo(act.created_at)}</span>
                    </div>
                  </div>
                );
              })}
              {(!data?.activities || data.activities.length === 0) && (
                <p className="text-sm text-[#757675] py-4 text-center">No recent activity</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
